import { HugeiconsIcon } from "@hugeicons/react";
import { CheckListIcon } from "@hugeicons-pro/core-bulk-rounded";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { PageHeader } from "@/components/layout/page-header";
import { GlobalFilterBar } from "@/components/filters/global-filter-bar";
import { FadeIn } from "@/components/ui/motion";
import { Badge } from "@/components/ui/badge";
import { PlatformLogo } from "@/components/ui/platform-logo";
import { RegionFlag } from "@/components/ui/region-flag";
import { BreakdownTable, type Column } from "@/components/dashboard/breakdown-table";
import { Skeleton } from "@/components/ui/skeleton";
import { useFilters } from "@/lib/filters";
import { useReference, brandById, platformById } from "@/lib/use-reference";
import { getCampaigns, SEGMENT_ITEMS, type CampaignSegment } from "@/lib/api/campaigns";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "@/components/ui/select";
import { compact } from "@/lib/format";
import { SYMBOL } from "@/lib/currency";
import type { Campaign, CurrencyCode } from "@/lib/api/types";

const TYPE_LABEL: Record<string, { label: string; variant: "default" | "info" | "warning" }> = {
  ecom: { label: "Ecom", variant: "default" },
  lighting: { label: "Awareness", variant: "info" },
  job: { label: "Job ads", variant: "warning" },
};

export function Campagnes() {
  const f = useFilters();
  const { brands, platforms } = useReference();
  const [segment, setSegment] = useState<CampaignSegment>("all");
  const nativeBing = f.channel === 'all' || platforms.find(p => p.id === f.channel)?.key === 'bing';

  const campaigns = useQuery({
    queryKey: ["campaigns", f.region, f.brand, f.channel, f.activity, segment, f.fromDate, f.toDate, nativeBing],
    queryFn: () => getCampaigns({ region: f.region, brand: f.brand, channel: f.channel, activity: f.activity, segment, from: f.fromDate, to: f.toDate, nativeBing }),
    retry: (count,error) => error.message === 'MICROSOFT_REPORT_PENDING' ? count < 60 : count < 2,
    retryDelay: 5000,
  });
  const rows = campaigns.data ?? [];

  const localMoney = (v: number, code: CurrencyCode) => `${SYMBOL[code] ?? "€"}${compact(v)}`;

  const columns: Column<Campaign>[] = [
    { key: "name", header: "Campagne", render: (c) => <span className="font-medium">{c.name}</span>, exportValue: (c) => c.name },
    { key: "market", header: "Markt", render: (c) => {
        if (!c.market_tag) return "—";
        const region = c.market_tag === "USA" ? "US" : c.market_tag;
        const known = region === "EU" || region === "US" || region === "CA";
        return (
          <div className="flex items-center gap-2">
            {known ? <RegionFlag region={region} size={18} /> : null}
            <span>{c.market_tag}</span>
          </div>
        );
      }, exportValue: (c) => c.market_tag ?? "" },
    { key: "type", header: "Type", render: (c) => {
        const t = TYPE_LABEL[c.type_tag ?? "ecom"] ?? TYPE_LABEL.ecom;
        return <Badge variant={t.variant} size="sm">{t.label}</Badge>;
      }, exportValue: (c) => (TYPE_LABEL[c.type_tag ?? "ecom"] ?? TYPE_LABEL.ecom).label },
    { key: "platform", header: "Channel", render: (c) => {
        const p = platformById(platforms, c.platform_id);
        if (!p) return "—";
        return (
          <div className="flex items-center gap-2.5">
            <PlatformLogo platform={p.key ?? ""} accentColor={p.accent_color} size={18} />
            <span>{p.name}</span>
          </div>
        );
      }, exportValue: (c) => platformById(platforms, c.platform_id)?.name ?? "" },
    { key: "brand", header: "Merk", render: (c) => c.brand_id
        ? (brandById(brands, c.brand_id)?.name ?? "—")
        : <span className="text-muted-foreground/70 italic">via naamconventie</span>,
      exportValue: (c) => c.brand_id ? (brandById(brands, c.brand_id)?.name ?? "") : "via naamconventie" },
    { key: "spend", header: "Uitgaven", align: "right", render: (c) => <span title={`${c.currency} ${c.spend.toFixed(2)}`}>{localMoney(c.spend, c.currency)}</span>, exportValue: (c) => `${c.currency} ${c.spend.toFixed(2)}` },
    { key: "revenue", header: "Conversiewaarde", align: "right", render: (c) => localMoney(c.revenue, c.currency), exportValue: (c) => `${SYMBOL[c.currency] ?? "€"}${c.revenue.toFixed(2)}` },
    { key: "impressions", header: "Impressies", align: "right", render: (c) => compact(c.impressions), exportValue: (c) => c.impressions },
    { key: "clicks", header: "Kliks", align: "right", render: (c) => compact(c.clicks), exportValue: (c) => c.clicks },
    { key: "applications", header: "Sollicitaties", align: "right", render: (c) => c.activity_type === "job_ads" ? compact(c.applications) : "—", exportValue: (c) => c.activity_type === "job_ads" ? c.applications : "" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Campagnes"
        icon={CheckListIcon}
        title="Campagnes (naamconventie)"
        description="Markt en type worden afgeleid uit de campagnenaam, ook voor accounts die niet per markt gesplitst zijn."
        tabs={<GlobalFilterBar showCurrency={false} />}
      />

      <FadeIn delay={0.05}>
        {campaigns.isPending ? (
          <div><Skeleton className="h-64 rounded-2xl" /><p className="mt-2 text-sm text-muted-foreground">Campagnecijfers laden. Microsoft maakt zo nodig eerst een exact rapport voor deze periode.</p></div>
        ) : campaigns.isError ? (
          <p role="alert">Campagnegegevens konden niet worden geladen. Probeer opnieuw; er worden geen geschatte bedragen getoond.</p>
        ) : (
          <BreakdownTable
            title={`Campagnes (${rows.length})`}
            description="Native campagnecijfers in de accountvaluta. Bing gebruikt het exacte Microsoft-periodeverslag; andere kanalen tellen dagcijfers op. Conversiewaarde kan afwijken van Converge-attributie."
            actions={
              <Select value={segment} onValueChange={(v) => setSegment((v as CampaignSegment) ?? "all")}>
                <SelectTrigger className="h-8 w-44" aria-label="Filter op segment">
                  <SelectValue>{SEGMENT_ITEMS.find(s => s.value === segment)?.label}</SelectValue>
                </SelectTrigger>
                <SelectPopup>
                  {SEGMENT_ITEMS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectPopup>
              </Select>
            }
            rows={rows}
            columns={columns}
            rowKey={(c) => c.id}
            empty="Geen campagnes voor deze selectie."
            exportName="Prado Pulse — Campagnes"
          />
        )}
      </FadeIn>
    </div>
  );
}
